import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
  Link,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  AddPhotoAlternate as AddPhotoAlternateIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { buildCompleteJobFormData } from '../../utils/completeJobFormData';
import { googleMapsQueryUrl, formatJobDeliveryLine } from '../../utils/jobMapsLink';

const DriverJobs = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [allActiveJobs, setAllActiveJobs] = useState([]);
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [currentDriverId, setCurrentDriverId] = useState(null);
  const [hasCurrentJob, setHasCurrentJob] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedJobForCompletion, setSelectedJobForCompletion] = useState(null);
  const [completionFormData, setCompletionFormData] = useState({
    diesel_delivered: '',
    def_delivered: '',
    completion_notes: '',
  });
  const [subJobs, setSubJobs] = useState([]);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [completionError, setCompletionError] = useState('');

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      // Get driver profile first to get driver ID
      let driverId = null;
      try {
        const profileRes = await api.get('/drivers/profile/');
        driverId = profileRes.data?.id;
        setCurrentDriverId(driverId);
        // Check if driver has a current job
        setHasCurrentJob(!!profileRes.data.current_job || !!profileRes.data.current_job_info);
      } catch (err) {
        console.error('Error fetching driver profile:', err);
        setHasCurrentJob(false);
      }
      
      // Fetch all active jobs and completed jobs with individual error handling
      let activeJobsData = [];
      let completedJobsData = [];
      
      try {
        const activeRes = await api.get('/drivers/jobs_active/');
        activeJobsData = activeRes.data || [];
      } catch (err) {
        console.error('Error fetching active jobs:', err);
        if (err.response?.status === 404) {
          // Driver profile might not exist, try alternative endpoint
          try {
            const jobsRes = await api.get('/jobs/active/');
            activeJobsData = jobsRes.data || [];
          } catch (altErr) {
            console.error('Error with alternative endpoint:', altErr);
          }
        }
      }
      
      try {
        const completedRes = await api.get('/drivers/jobs_completed/');
        completedJobsData = completedRes.data || [];
      } catch (err) {
        console.error('Error fetching completed jobs:', err);
      }
      
      setAllActiveJobs(activeJobsData);
      setCompletedJobs(completedJobsData);
      
      // Try to fetch assigned jobs, fallback to filtering from active jobs
      try {
        const assignedRes = await api.get('/drivers/jobs_assigned/');
        setAssignedJobs(assignedRes.data || []);
      } catch (error) {
        // If endpoint doesn't exist or fails, filter assigned jobs from active jobs
        console.log('jobs_assigned endpoint not available, filtering from active jobs');
        const assigned = activeJobsData.filter(job => 
          (job.driver_name || job.driver) && 
          (job.driver === driverId || job.driver?.id === driverId)
        );
        setAssignedJobs(assigned);
      }
    } catch (error) {
      console.error('Unexpected error fetching jobs:', error);
      // Set empty arrays on error
      setAllActiveJobs([]);
      setAssignedJobs([]);
      setCompletedJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptJob = async (jobId) => {
    if (!jobId) {
      alert('Invalid job ID');
      return;
    }
    
    try {
      // Get driver profile first to get driver ID
      const profileRes = await api.get('/drivers/profile/');
      const driverId = profileRes.data?.id;
      
      if (!driverId) {
        alert('Driver profile not found');
        return;
      }
      
      await api.post(`/drivers/${driverId}/accept_job/`, { job_id: jobId });
      // Refresh jobs after accepting
      await fetchJobs();
    } catch (error) {
      console.error('Error accepting job:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error accepting job. Please try again.';
      alert(errorMsg);
    }
  };

  const handleRejectJob = async (jobId) => {
    if (!jobId) {
      alert('Invalid job ID');
      return;
    }
    
    if (!window.confirm('Are you sure you want to reject this job? This will unassign you from the job.')) {
      return;
    }
    
    try {
      // Get driver profile first to get driver ID
      const profileRes = await api.get('/drivers/profile/');
      const driverId = profileRes.data?.id;
      
      if (!driverId) {
        alert('Driver profile not found');
        return;
      }
      
      await api.post(`/drivers/${driverId}/reject_job/`, { job_id: jobId });
      alert('Job rejected successfully');
      // Refresh jobs after rejecting
      await fetchJobs();
    } catch (error) {
      console.error('Error rejecting job:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error rejecting job. Please try again.';
      alert(errorMsg);
    }
  };

  const handleOpenCompleteDialog = async (job) => {
    setDeliveryPhotos([]);
    setSelectedJobForCompletion(job);
    setCompletionFormData({
      diesel_delivered: job.diesel_quantity || '',
      def_delivered: job.def_quantity || '',
      completion_notes: '',
    });
    setSubJobs([]);
    setLocationError('');
    setCompletionError('');
    setCompleteDialogOpen(true);
    
    // Get current GPS location
    setGettingLocation(true);
    try {
      const location = await getCurrentLocation();
      setCompletionFormData(prev => ({
        ...prev,
        latitude: location.latitude,
        longitude: location.longitude,
      }));
    } catch (error) {
      console.error('Error getting location:', error);
      setLocationError('Could not get GPS location. You can still complete the job without location.');
    } finally {
      setGettingLocation(false);
    }
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser. Please use a modern browser with GPS support.'));
        return;
      }

      // Check if we're in a secure context (HTTPS required for geolocation on mobile)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        reject(new Error('GPS location requires HTTPS. Please access the app via a secure connection.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Verify we got valid coordinates
          if (position.coords.latitude == null || position.coords.longitude == null) {
            reject(new Error('Invalid GPS coordinates received. Please ensure location services are enabled.'));
            return;
          }
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          // Provide user-friendly error messages based on error code
          let errorMessage = 'Failed to get GPS location. ';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += 'Location permission denied. Please enable location access in your browser settings and allow location access for this site.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += 'GPS position unavailable. Please ensure location services are enabled on your device and you have a clear view of the sky.';
              break;
            case error.TIMEOUT:
              errorMessage += 'GPS request timed out. Please try again. Make sure you are outdoors or near a window for better GPS signal.';
              break;
            default:
              errorMessage += `Error: ${error.message || 'Unknown error'}. Please check your device GPS settings.`;
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true, // Use GPS on mobile devices
          timeout: 15000, // Increased timeout for mobile GPS (15 seconds)
          maximumAge: 10000, // Accept cached position up to 10 seconds old (mobile-friendly)
        }
      );
    });
  };

  const handleCloseCompleteDialog = () => {
    setDeliveryPhotos((prev) => {
      prev.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
      return [];
    });
    setCompleteDialogOpen(false);
    setSelectedJobForCompletion(null);
    setCompletionFormData({
      diesel_delivered: '',
      def_delivered: '',
      completion_notes: '',
    });
    setSubJobs([]);
    setLocationError('');
    setCompletionError('');
  };
  
  const handleAddSubJob = () => {
    // Pre-populate with selected job's address if available
    const defaultAddress = selectedJobForCompletion ? {
      delivery_address: selectedJobForCompletion.delivery_address || selectedJobForCompletion.job_location?.split(',')[0] || '',
      delivery_city: selectedJobForCompletion.delivery_city || '',
      delivery_state: selectedJobForCompletion.delivery_state || '',
      delivery_zip: selectedJobForCompletion.delivery_zip || '',
    } : {
      delivery_address: '',
      delivery_city: '',
      delivery_state: '',
      delivery_zip: '',
    };
    
    setSubJobs([...subJobs, {
      ...defaultAddress,
      delivery_notes: '',
      diesel_delivered: '',
      def_delivered: '',
      latitude: null,
      longitude: null,
    }]);
  };
  
  const handleRemoveSubJob = (index) => {
    setSubJobs(subJobs.filter((_, i) => i !== index));
  };
  
  const handleSubJobChange = (index, field, value) => {
    const updated = [...subJobs];
    updated[index] = { ...updated[index], [field]: value };
    setSubJobs(updated);
  };
  
  const handleGetLocationForSubJob = async (index) => {
    try {
      const location = await getCurrentLocation();
      handleSubJobChange(index, 'latitude', location.latitude);
      handleSubJobChange(index, 'longitude', location.longitude);
    } catch (error) {
      console.error('Error getting location for sub-job:', error);
    }
  };

  const handleCompletionFormChange = (e) => {
    setCompletionFormData({
      ...completionFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleDeliveryPhotoInput = async (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (!selectedFiles.length) return;
    e.target.value = '';
    setCompletionError('');

    let plat = completionFormData.latitude;
    let plng = completionFormData.longitude;
    if (plat == null || plng == null) {
      try {
        const loc = await getCurrentLocation();
        plat = loc.latitude;
        plng = loc.longitude;
        setCompletionFormData((p) => ({ ...p, latitude: plat, longitude: plng }));
      } catch (err) {
        console.warn('Could not get GPS for delivery photos:', err);
      }
    }

    setDeliveryPhotos((prev) => {
      const remaining = 10 - prev.length;
      if (remaining <= 0) {
        setCompletionError('Maximum of 10 delivery photos.');
        return prev;
      }
      const slice = selectedFiles.slice(0, remaining);
      if (selectedFiles.length > remaining) {
        setCompletionError(`Only ${remaining} more photo(s) allowed (10 maximum).`);
      }
      const next = [...prev];
      slice.forEach((file) => {
        next.push({
          id: `${Date.now()}-${Math.random()}`,
          file,
          previewUrl: URL.createObjectURL(file),
          latitude: plat ?? null,
          longitude: plng ?? null,
        });
      });
      return next;
    });
  };

  const removeDeliveryPhoto = (id) => {
    setDeliveryPhotos((prev) => {
      const row = prev.find((p) => p.id === id);
      if (row?.previewUrl) URL.revokeObjectURL(row.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleCompleteJob = async () => {
    if (!selectedJobForCompletion) {
      setCompletionError('No job selected');
      return;
    }

    setCompletionError('');

    // Validate required fields
    if (!completionFormData.diesel_delivered && !completionFormData.def_delivered) {
      setCompletionError('Please enter at least one quantity (diesel or DEF)');
      return;
    }

    try {
      // Get driver profile first to get driver ID
      const profileRes = await api.get('/drivers/profile/');
      const driverId = profileRes.data?.id;
      
      if (!driverId) {
        setCompletionError('Driver profile not found');
        return;
      }

      const subJobsPayload =
        subJobs.length > 0
          ? subJobs.map((subJob) => ({
              delivery_address: subJob.delivery_address || '',
              delivery_city: subJob.delivery_city || '',
              delivery_state: subJob.delivery_state || '',
              delivery_zip: subJob.delivery_zip || '',
              delivery_notes: subJob.delivery_notes || '',
              diesel_delivered: subJob.diesel_delivered ? parseFloat(subJob.diesel_delivered) : 0,
              def_delivered: subJob.def_delivered ? parseFloat(subJob.def_delivered) : 0,
              latitude: subJob.latitude || null,
              longitude: subJob.longitude || null,
            }))
          : null;

      const formData = buildCompleteJobFormData({
        jobId: selectedJobForCompletion.id,
        dieselDelivered: completionFormData.diesel_delivered
          ? parseFloat(completionFormData.diesel_delivered)
          : 0,
        defDelivered: completionFormData.def_delivered ? parseFloat(completionFormData.def_delivered) : 0,
        completionNotes: completionFormData.completion_notes || '',
        latitude: completionFormData.latitude,
        longitude: completionFormData.longitude,
        subJobsPayload,
        deliveryPhotos,
      });

      const response = await api.post(`/drivers/${driverId}/complete_job/`, formData);
      
      // Check if sub-jobs were created
      if (response.data.sub_jobs_created !== undefined) {
        const subJobsMsg = response.data.sub_jobs_created > 0 
          ? ` with ${response.data.sub_jobs_created} sub-delivery stop${response.data.sub_jobs_created > 1 ? 's' : ''}`
          : '';
        alert(`Job marked as complete successfully${subJobsMsg}!`);
        
        // Show warnings if any sub-jobs failed
        if (response.data.sub_jobs_errors && response.data.sub_jobs_errors.length > 0) {
          console.warn('Some sub-jobs failed to create:', response.data.sub_jobs_errors);
          alert(`Warning: Some sub-deliveries failed to save. Please check the job details.`);
        }
      } else {
        alert('Job marked as complete successfully!');
      }
      
      handleCloseCompleteDialog();
      // Refresh jobs after completing
      await fetchJobs();
    } catch (error) {
      console.error('Error completing job:', error);
      const errorMsg = error.response?.data?.error || 
                      error.response?.data?.message || 
                      'Error completing job. Please try again.';
      setCompletionError(errorMsg);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      accepted: 'info',
      active: 'primary',
      complete: 'success',
      cancelled: 'error',
    };
    return colors[status] || 'default';
  };

  // Safely get jobs based on tab, with fallback to empty array
  const jobs = tabValue === 0 
    ? (allActiveJobs || []) 
    : tabValue === 1 
    ? (assignedJobs || []) 
    : (completedJobs || []);

  return (
    <Layout>
      <Container>
        <Typography variant="h4" gutterBottom>
          Jobs
        </Typography>
        {hasCurrentJob && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You currently have an active job. Please complete or reject your current job before accepting a new one.
          </Alert>
        )}
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label={`All Active Jobs (${allActiveJobs?.length || 0})`} />
          <Tab label={`My Assigned Jobs (${assignedJobs?.length || 0})`} />
          <Tab label={`Completed (${completedJobs?.length || 0})`} />
        </Tabs>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Fuel (US gal)</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned Driver</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!jobs || jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No jobs found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => {
                  // Check if job is assigned to current driver
                  // job.driver can be a UUID string or an object with id
                  const jobDriverId = typeof job.driver === 'string' 
                    ? job.driver 
                    : job.driver?.id || job.driver;
                  const isMyJob = currentDriverId && jobDriverId && (
                    jobDriverId === currentDriverId || 
                    String(jobDriverId) === String(currentDriverId)
                  );
                  
                  const canAccept = job.job_status === 'pending' && !job.driver && !job.driver_name && !hasCurrentJob;
                  const canReject = isMyJob && (job.job_status === 'accepted' || job.job_status === 'cancelled');
                  const canComplete = isMyJob && (job.job_status === 'accepted' || job.job_status === 'active');
                  const isAssigned = job.driver_name && job.job_status !== 'complete' && job.job_status !== 'accepted';
                  
                  return (
                    <TableRow key={job.id}>
                      <TableCell>{job.id ? job.id.substring(0, 8) : 'N/A'}</TableCell>
                      <TableCell>{job.customer_name || 'N/A'}</TableCell>
                      <TableCell>
                        {(() => {
                          const line = formatJobDeliveryLine(job);
                          return line ? (
                            <Link href={googleMapsQueryUrl(line)} target="_blank" rel="noopener noreferrer">
                              {line}
                            </Link>
                          ) : (
                            'N/A'
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        {job.diesel_quantity > 0 && `${job.diesel_quantity} gal Diesel`}
                        {job.diesel_quantity > 0 && job.def_quantity > 0 && ' / '}
                        {job.def_quantity > 0 && `${job.def_quantity} gal DEF`}
                        {(!job.diesel_quantity || job.diesel_quantity === 0) && (!job.def_quantity || job.def_quantity === 0) && 'N/A'}
                      </TableCell>
                      <TableCell>{job.frequency || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={job.job_status || 'unknown'}
                          color={getStatusColor(job.job_status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{job.driver_name || 'Not assigned'}</TableCell>
                      <TableCell>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {canAccept && (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => handleAcceptJob(job.id)}
                            >
                              Accept
                            </Button>
                          )}
                          {!canAccept && job.job_status === 'pending' && !job.driver && !job.driver_name && hasCurrentJob && (
                            <Chip 
                              label="Complete current job first" 
                              color="warning" 
                              size="small"
                              title="You cannot accept a new job while you have an active job"
                            />
                          )}
                          {!canAccept && job.job_status === 'pending' && !job.driver && !job.driver_name && hasCurrentJob && (
                            <Chip 
                              label="Complete current job first" 
                              color="warning" 
                              size="small"
                              title="You cannot accept a new job while you have an active job"
                            />
                          )}
                          {canReject && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<CancelIcon />}
                              onClick={() => handleRejectJob(job.id)}
                            >
                              Reject
                            </Button>
                          )}
                          {canComplete && (
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircleIcon />}
                              onClick={() => handleOpenCompleteDialog(job)}
                            >
                              Mark Complete
                            </Button>
                          )}
                          {isAssigned && !canComplete && (
                            <Chip label="Assigned" color="info" size="small" />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Complete Job Dialog */}
        <Dialog 
          open={completeDialogOpen} 
          onClose={handleCloseCompleteDialog} 
          maxWidth="sm" 
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              m: { xs: 0, sm: 2 },
            },
          }}
        >
          <DialogTitle>Complete Job</DialogTitle>
          <DialogContent>
            {completionError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCompletionError('')}>
                {completionError}
              </Alert>
            )}

            {locationError && (
              <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setLocationError('')}>
                {locationError}
              </Alert>
            )}

            {selectedJobForCompletion && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Job: {selectedJobForCompletion.id?.substring(0, 8)}
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  Location:{' '}
                  {(() => {
                    const line = formatJobDeliveryLine(selectedJobForCompletion);
                    return line ? (
                      <Link href={googleMapsQueryUrl(line)} target="_blank" rel="noopener noreferrer">
                        {line}
                      </Link>
                    ) : (
                      'N/A'
                    );
                  })()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customer: {selectedJobForCompletion.customer_name}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Delivered Quantities
              </Typography>
              
              <TextField
                name="diesel_delivered"
                label="Diesel delivered (liters)"
                type="number"
                value={completionFormData.diesel_delivered}
                onChange={handleCompletionFormChange}
                fullWidth
                inputProps={{ 
                  step: '0.01',
                  min: 0
                }}
                helperText="Diesel delivered, in liters"
              />

              <TextField
                name="def_delivered"
                label="DEF delivered (liters)"
                type="number"
                value={completionFormData.def_delivered}
                onChange={handleCompletionFormChange}
                fullWidth
                inputProps={{ 
                  step: '0.01',
                  min: 0
                }}
                helperText="DEF delivered, in liters"
              />

              <TextField
                name="completion_notes"
                label="Completion Notes"
                multiline
                rows={3}
                value={completionFormData.completion_notes}
                onChange={handleCompletionFormChange}
                fullWidth
                helperText="Any additional notes about the delivery"
              />

              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Delivery photos (optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Up to 10 images (JPEG, PNG, or WebP). Each photo is tagged with GPS from this device when
                  available; otherwise coordinates may be read from the photo (EXIF) or from the completion
                  location above.
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-block', mt: 0.5 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<AddPhotoAlternateIcon />}
                    tabIndex={-1}
                    aria-hidden
                    sx={{ pointerEvents: 'none' }}
                    disabled={deliveryPhotos.length >= 10}
                  >
                    Add photos ({deliveryPhotos.length}/10)
                  </Button>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,image/*"
                    multiple
                    disabled={deliveryPhotos.length >= 10}
                    aria-label="Add delivery photos"
                    onChange={handleDeliveryPhotoInput}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%',
                      opacity: 0,
                      cursor: deliveryPhotos.length >= 10 ? 'not-allowed' : 'pointer',
                      fontSize: '16px',
                      zIndex: 2,
                    }}
                  />
                </Box>
                {deliveryPhotos.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    {deliveryPhotos.map((p) => (
                      <Box key={p.id} sx={{ position: 'relative', width: 88, height: 88 }}>
                        <Box
                          component="img"
                          src={p.previewUrl}
                          alt=""
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeDeliveryPhoto(p.id)}
                          sx={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            p: 0.25,
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>

              {gettingLocation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Getting GPS location...
                  </Typography>
                </Box>
              )}

              {completionFormData.latitude != null && completionFormData.longitude != null && (
                <Alert severity="success" sx={{ mt: 1 }}>
                  Location captured: {completionFormData.latitude.toFixed(6)}, {completionFormData.longitude.toFixed(6)}
                </Alert>
              )}
            </Box>

            {/* Sub-Jobs Section */}
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Additional Delivery Stops (Sub-Jobs)
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddSubJob}
                  size="small"
                  variant="outlined"
                >
                  Add Stop
                </Button>
              </Box>

              {subJobs.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No additional stops. Click "Add Stop" to add multiple delivery locations.
                </Typography>
              )}

              {subJobs.map((subJob, index) => (
                <Paper key={index} elevation={2} sx={{ p: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Stop {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveSubJob(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Delivery Address"
                      value={subJob.delivery_address}
                      onChange={(e) => handleSubJobChange(index, 'delivery_address', e.target.value)}
                      fullWidth
                      size="small"
                    />

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="City"
                        value={subJob.delivery_city}
                        onChange={(e) => handleSubJobChange(index, 'delivery_city', e.target.value)}
                        fullWidth
                        size="small"
                      />
                      <TextField
                        label="State"
                        value={subJob.delivery_state}
                        onChange={(e) => handleSubJobChange(index, 'delivery_state', e.target.value)}
                        size="small"
                        sx={{ width: 100 }}
                        inputProps={{ maxLength: 2 }}
                      />
                      <TextField
                        label="ZIP"
                        value={subJob.delivery_zip}
                        onChange={(e) => handleSubJobChange(index, 'delivery_zip', e.target.value)}
                        size="small"
                        sx={{ width: 120 }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="Diesel delivered (liters)"
                        type="number"
                        value={subJob.diesel_delivered}
                        onChange={(e) => handleSubJobChange(index, 'diesel_delivered', e.target.value)}
                        fullWidth
                        size="small"
                        inputProps={{ step: '0.01', min: 0 }}
                      />
                      <TextField
                        label="DEF delivered (liters)"
                        type="number"
                        value={subJob.def_delivered}
                        onChange={(e) => handleSubJobChange(index, 'def_delivered', e.target.value)}
                        fullWidth
                        size="small"
                        inputProps={{ step: '0.01', min: 0 }}
                      />
                    </Box>

                    <TextField
                      label="Delivery Notes"
                      multiline
                      rows={2}
                      value={subJob.delivery_notes}
                      onChange={(e) => handleSubJobChange(index, 'delivery_notes', e.target.value)}
                      fullWidth
                      size="small"
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        startIcon={<LocationIcon />}
                        onClick={() => handleGetLocationForSubJob(index)}
                        size="small"
                        variant="outlined"
                      >
                        Get Location
                      </Button>
                      {subJob.latitude != null && subJob.longitude != null && (
                        <Typography variant="body2" color="text.secondary">
                          {subJob.latitude.toFixed(6)}, {subJob.longitude.toFixed(6)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCompleteDialog}>Cancel</Button>
            <Button 
              onClick={handleCompleteJob} 
              variant="contained" 
              color="success"
              disabled={gettingLocation}
            >
              Mark as Complete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default DriverJobs;



